import { useQuery } from '@tanstack/react-query';

import { settings } from '@/shared/api/resources';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';

/**
 * Escolha de um motivo padronizado.
 *
 * O motivo vira relatorio, e por isso vem de uma lista fechada: "cliente desistiu",
 * "desistencia" e "Desistencia do cliente" sao a mesma coisa para quem opera e tres
 * coisas diferentes para quem soma.
 *
 * Quando a empresa nao tem catalogo naquele escopo, o campo volta a aceitar texto
 * livre. Bloquear a recusa por falta de catalogo seria pior: a decisao precisa
 * acontecer, e o texto livre e melhor do que nenhum motivo.
 */
export interface MotivoPadronizadoProps {
  escopo: 'credito' | 'subscricao' | 'juridico' | 'cancelamento';
  label: string;
  hint?: string;
  value: string;
  onChange: (valor: string) => void;
  disabled?: boolean;
  error?: string | undefined;
}

export function MotivoPadronizado({
  escopo,
  label,
  hint,
  value,
  onChange,
  disabled,
  error,
}: MotivoPadronizadoProps) {
  const consulta = useQuery({
    queryKey: ['decline-reasons', escopo],
    queryFn: ({ signal }) => settings.declineReasons({ scope: escopo }, signal),
    // O catalogo muda raramente e e lido em varias telas: uma janela mais longa
    // evita uma consulta por abertura de painel.
    staleTime: 5 * 60_000,
  });

  const motivos = consulta.data ?? [];

  // Enquanto o catalogo carrega, o campo continua utilizavel como texto: uma
  // decisao nao pode esperar por uma lista.
  if (consulta.isPending || motivos.length === 0) {
    return (
      <Textarea
        label={label}
        value={value}
        disabled={disabled}
        hint={
          consulta.isPending
            ? hint
            : 'Nenhum motivo padronizado cadastrado para este caso. Configure em Configuracoes para que o relatorio agrupe.'
        }
        error={error}
        onChange={(evento) => onChange(evento.target.value)}
      />
    );
  }

  return (
    <Select
      label={label}
      value={value}
      placeholder="Selecione o motivo"
      disabled={disabled}
      hint={hint}
      error={error}
      options={motivos.map((motivo) => ({ value: motivo.label, label: motivo.label }))}
      onChange={(evento) => onChange(evento.target.value)}
    />
  );
}
